import { useContext } from 'react';
import { ENDPOINTS } from 'pages/common/consts/endpoints';
import { TProjectConfigurationParameter } from 'pages/common/types';
import { TCalculateData, TComplimentaryProductInfo } from 'pages/dimensions/store/dimensions';
import { roundToTwo } from 'common/helpers/round-to-two.helper';
import { DataContext } from 'entries/common/widgets/saved-project-configuration/context';
import { useRegionMismatch } from '../components/AddToCart/hooks/use-region-mismatch';

type TEditConfigurationProps = {
  calculateParams: TCalculateData;
  updatedConfigurationParams: TProjectConfigurationParameter[];
  regionId?: string;
};

export const useEditConfigurationRequest = () => {
  const { data } = useContext(DataContext);
  const { isRegionMismatch } = useRegionMismatch();

  const editConfigurationRequest = async ({
    calculateParams,
    updatedConfigurationParams,
    regionId,
  }: TEditConfigurationProps) => {
    const mainProduct = {
      itemId: '1',
      code: calculateParams.items[0].productId,
      type: 'PRODUCT',
      name: calculateParams?.items?.[0]?.displayedName?.value?.[0],
      quantity: calculateParams.items[0].total.requiredQuantity,
      price: calculateParams.items[0].salesPriceValueInConsumerUnitValue ?? 0,
      complementary: false,
    };

    const complimentaryProducts = calculateParams.complimentaryProductInfo.map((product, index) => ({
      itemId: String(index + 2),
      code: product.productId,
      type: 'PRODUCT',
      name: product?.displayedName?.value?.[0],
      quantity: product.total?.requiredQuantity ?? 1,
      price: product.salesPriceValueInConsumerUnitValue ?? 0,
      complementary: true,
    }));

    const getCheapestProductFromGroup = (products: TComplimentaryProductInfo[]) => {
      if (!products.length) return null;

      return products.reduce((cheapest, current) => {
        const cheapestPrice =
          (cheapest.salesPriceValueInConsumerUnitValue ?? 0) * (cheapest.total.requiredQuantity ?? 0);
        const currentPrice =
          (current.salesPriceValueInConsumerUnitValue ?? 0) * (current.total.requiredQuantity ?? 0);

        return currentPrice < cheapestPrice ? current : cheapest;
      });
    };

    const cheapestGroupProducts = calculateParams.complimentaryGroups
      .filter(group => group.products?.length > 0)
      .map(group => {
        const cheapestProduct = getCheapestProductFromGroup(group.products);
        if (!cheapestProduct) return null;

        return {
          code: cheapestProduct.productId,
          name: cheapestProduct?.displayedName?.value?.[0] ?? '',
          quantity: cheapestProduct.total.requiredQuantity,
          price: cheapestProduct.salesPriceValueInConsumerUnitValue ?? 0,
          onePiece: cheapestProduct.total.onePiece,
        };
      })
      .filter(product => product !== null)
      .map((product, index) => ({
        itemId: String(complimentaryProducts.length + index + 2),
        code: product?.code ?? '',
        type: 'PRODUCT',
        name: product?.name ?? '',
        quantity: product?.quantity ?? 0,
        price: product?.price ?? 0,
        complementary: true,
      }));

    const generalItems = [mainProduct, ...complimentaryProducts, ...cheapestGroupProducts];
    const mainProductTotalPrice = mainProduct.price * mainProduct.quantity;
    const complementaryProductsTotalPrice = generalItems
      .filter(item => item.complementary)
      .reduce((total, product) => total + product.price * product.quantity, 0);
    const totalPrice = mainProductTotalPrice + complementaryProductsTotalPrice;

    data!.api
      .patch(ENDPOINTS.EDIT_CONFIGURATION(data!.id), {
        ...(isRegionMismatch ? { regionId } : {}),
        approximateTotalAmount: roundToTwo(totalPrice),
        configurationParameters: updatedConfigurationParams,
        generalItems,
      })
      // eslint-disable-next-line no-console
      .catch((e: Error) => console.error('Patch configuration failed', e));
  };

  return { editConfigurationRequest };
};
